import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { config } from '../../../config';
import axios from 'axios';
import { useMemo } from 'react';
import { getWorkflowDefinition } from '../definitions';
import type { WorkflowProgress, WorkflowResponse } from '../types';

const backendApi = config.backendApi;

const fetchWorkflow = async (workflowId: string): Promise<WorkflowResponse> => {
  const response = await axios.get<WorkflowResponse>(
    `${backendApi}/api/workflows/${workflowId}`,
    { withCredentials: true }
  );
  return response.data;
};

const dismissWorkflow = async (workflowId: string): Promise<void> => {
  await axios.post(
    `${backendApi}/api/workflows/${workflowId}/dismiss`,
    {},
    { withCredentials: true }
  );
};

export function useWorkflow(workflowId: string) {
  const queryClient = useQueryClient();
  const definition = getWorkflowDefinition(workflowId);

  if (!definition) {
    throw new Error(`Unknown workflow: ${workflowId}`);
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['workflow', workflowId],
    queryFn: () => fetchWorkflow(workflowId),
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: true,
  });

  const dismissMutation = useMutation({
    mutationFn: () => dismissWorkflow(workflowId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['workflow', workflowId] });
    },
  });

  const progress: WorkflowProgress = useMemo(() => {
    if (!data) {
      return { completed: 0, total: 0 };
    }

    const completed = Object.values(data.steps).filter(Boolean).length;
    const total = Object.keys(data.steps).length;

    return { completed, total };
  }, [data]);

  return {
    definition,
    steps: data?.steps,
    scope: data?.scope ?? definition.scope,
    dismissed: data?.dismissed ?? false,
    progress,
    dismiss: dismissMutation.mutate,
    isLoading,
    error,
  };
}
